import { AfterViewInit, Component, inject, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AccountService } from "../lib/account.service";
import { FetchStatus } from "../lib/util";
import { LoginModalComponent } from "../support/login-modal/login-modal.component";

@Component({
  selector: "app-landing",
  templateUrl: "./landing.page.html",
  styleUrls: ["./landing.page.css"],
})
export class LandingPage implements AfterViewInit {
  private account = inject(AccountService);
  private route = inject(ActivatedRoute);
  private emailVerificationToken: string | null = null;
  private passwordResetToken: string | null = null;

  // Allows access to update child component.
  @ViewChild(LoginModalComponent) loginModal!: LoginModalComponent;

  /**
   * Loads after all of the child components have loaded, in this case the LoginModal.
   * Includes special handling in the cases where the user is hitting the landing
   * page after clicking on a link in an email (either for confirming their account
   * or resetting their password).
   * @returns
   */
  async ngAfterViewInit() {

    // Check to see if we are coming from a verify email or
    // password reset email.
    if (this.isEmailVerification()) {

      // Call API to verify email. Since we don't need any further 'input' from
      // the user, we can call API directly. No need to update anything within
      // the child component login modal.
      this.account.verifyEmail(
        this.emailVerificationToken!,
        async () => {

          // Notify that the email has been verified, and that they should be able to login now.
          await this.account.alert("Email Verification", "Your email has successfully been verified. Please proceed to login with your account.");

          this.account.goHome();
        },
        async (_err: FetchStatus) => {

          await this.account.alert("Error", "There was an issue verifying email address.");

          this.account.goHome();
        }
      )
    }
    else if (this.isPasswordReset()) {

      // Update reset token property in the child component (Login Modal).
      this.loginModal.resetPasswordToken = this.passwordResetToken!;

      // Update Login Modal state so that it shows the password reset screen.
      // The login modal will handle things from here, no further action.
      this.loginModal.state = this.loginModal.LoginModalState.ResetPassword;
    }

    if (this.account.isSignedIn()) {
      await this.account.goHome();
    }
  }

  /**
   * Checks to see if there is an email_verify_token value in the request body. If so,
   * this indicates that the page isn't loading from a normal request, it's coming from
   * a user clicking on a "confirm email address" link which will lead a user to the login
   * screen, and we should show confirmation that they have confirmed their email.
   * @returns
   */
  isEmailVerification(): boolean {
    this.route.queryParams.subscribe(params => {
      this.emailVerificationToken = params['email_verify_token']
    });
    if (this.emailVerificationToken != null) {
      return true;
    }
    else {
      return false;
    }
  }

  /**
   * Checks to see if there is a password_reset_token value in the request body. If so,
   * this indicates that the page isn't loading from a normal request, it's coming from
   * a user clicking on reset password link in an email which should lead a user to the reset
   * password screen, and we should show confirmation that they have reset their password.
   * @returns
   */
  isPasswordReset(): boolean {
    this.route.queryParams.subscribe(params => {
      this.passwordResetToken = params['password_reset_token']
    });
    if (this.passwordResetToken != null) {
      return true;
    }
    else {
      return false;
    }
  }
}
